import React, { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Select, Button, Space, Row, Col, Tag, Input } from 'antd';
import { PlusOutlined, MinusOutlined, EditOutlined, DeleteOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import equal from 'fast-deep-equal/react';

import Table from 'components/Table';
import SelectOrAdd from 'components/SelectOrAdd';
import { useMutation } from 'hooks/apollo';
import { openGoogleTranslate } from 'components/TranslationButton';
import theme from 'shared/constants/theme.json';
import globalMessages from 'translations/messages';
import Brain from 'components/Icons/Brain';
import messages from './messages';
import RenameTag from './RenameTag';

const { Option, OptGroup } = Select;
const { CheckableTag } = Tag;

const ADD_THEME = gql`
  mutation($text: String!, $questionIds: [ID!]!) {
    addTheme(text: $text, questionIds: $questionIds) {
      id
    }
  }
`;

const REMOVE_THEME = gql`
  mutation($id: ID!) {
    removeTheme(id: $id) {
      id
    }
  }
`;

const UNTAG_COMMENTS = gql`
  mutation($input: TagCommentsInput!) {
    untagComments(input: $input) {
      id
      tags {
        id
      }
      autoTag {
        id
        tags { id }
        sentiments { id }
      }
    }
  }
`;

const TAG_COMMENTS = gql`
  mutation($input: TagCommentsInput!) {
    tagComments(input: $input) {
      id
      tags {
        id
      }
      autoTag {
        id
        tags { id }
        sentiments { id }
      }
    }
  }
`;

const TagCommentsManualTab = ({
  $t,
  questionIds,
  refetchTags,
  sentiments,
  themes,
  tagsLoading,
  questionsLoading,
  responsesLoading,
  comments,
  questionColumns,
  reportComment,
  onVisibleColumnsChange,
}) => {
  const [selectedTagId, setSelectedTagId] = useState();
  const [renameTag, setRenameTag] = useState(false);
  const [currentFilters, setCurrentFilters] = useState();
  const [addThemeMutation, { loading: createThemeLoading }] = useMutation(ADD_THEME);
  const [removeThemeMutation, { loading: removeThemeLoading }] = useMutation(REMOVE_THEME);
  const [tagMutation, { loading: tagCommentsLoading }] = useMutation(TAG_COMMENTS);
  const [untagMutation, { loading: untagCommentsLoading }] = useMutation(UNTAG_COMMENTS);
  const selectedTag = themes.find(t => t.id === selectedTagId) || sentiments.find(t => t.id === selectedTagId);

  const handleTagsMutation = async ({ action, tag = selectedTag, questionResponseIds }) => {
    if (!tag?.id || !questionResponseIds?.length) {
      return;
    }

    const variables = {
      input: {
        tagId: tag.id,
        questionResponseIds,
      },
    };

    if (action === 'remove') {
      await untagMutation({ variables });
    } else {
      await tagMutation({ variables });
    }

    refetchTags();
  };

  const handleAddTheme = async text => {
    await addThemeMutation({ variables: { text, questionIds } });
    refetchTags();
  };

  const handleRemoveTheme = async () => {
    await removeThemeMutation({ variables: { id: selectedTagId } });
    refetchTags();
    setSelectedTagId(null);
  };

  const onRenameTheme = () => {
    setRenameTag(false);
    refetchTags();
  };

  const columns = useMemo(() => {
    const renderTags = ({ tags = [], autoTags = [], allTags = [], rowData, translate }) => allTags.map(({ id, text, type }) => {
      const tagged = tags.includes(id);
      const autoTagged = autoTags.includes(id);
      const checked = tagged || autoTagged;
      return (
        <CheckableTag
          onChange={state => handleTagsMutation({ action: state ? 'add' : 'remove', tag: { id, text, type }, questionResponseIds: [rowData.id] })}
          key={text}
          checked={checked}
          style={{ borderColor: checked ? '' : theme.borderColor, backgroundColor: checked && theme[text] }}
        >
          {autoTagged ? <Brain style={{ marginRight: 4, fontSize: '0.8em' }} /> : null}
          {translate && messages[text] ? $t(messages[text]) : text}
        </CheckableTag>
      );
    });

    return [
      {
        title: <FormattedMessage {...globalMessages.date} />,
        dataIndex: 'date',
        type: 'date',
        width: 160,
        defaultSortOrder: 'descend',
      },
      {
        title: <FormattedMessage {...globalMessages.text} />,
        dataIndex: 'text',
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Input
              id="tag-comments-search-input"
              placeholder="Search"
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => confirm({ closeDropdown: true })}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm({ closeDropdown: true })}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Search
              </Button>
              <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: 90 }}>
                Reset
              </Button>
            </Space>
          </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? theme['primary-color'] : undefined }} />,
        onFilter: (value, record) => record.text?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: {
          onOpenChange: open => open && setTimeout(() => document.querySelector('#tag-comments-search-input')?.focus(), 100),
        },
      },
      {
        title: (
          <>
            <Brain style={{ marginRight: 4 }} />
            Translation
          </>
        ),
        dataIndex: 'translation',
        visible: false,
      },
      {
        title: <FormattedMessage {...messages.theme} />,
        width: 150,
        dataIndex: 'themes',
        render: (tags, rowData) => renderTags({ tags, autoTags: rowData.autoThemes, allTags: themes, rowData }),
        filters: [{ text: <i>Without tag</i>, value: false }, ...themes.map(({ id, text }) => ({ text, value: id }))],
        onFilter: (value, record) => (value ? record.themes?.find(t => t === value) : !record.themes?.length),
        sorter: false,
      },
      {
        title: <FormattedMessage {...messages.sentiment} />,
        width: 100,
        align: 'center',
        dataIndex: 'sentiments',
        render: (tags, rowData) => renderTags({ tags, autoTags: rowData.autoSentiments, allTags: sentiments, rowData, translate: true }),
        filters: [{ text: <i>Without sentiment</i>, value: false }, ...sentiments.map(({ id, text }) => ({ text, value: id }))],
        onFilter: (value, record) => (value ? record.sentiments?.find(t => t === value) : !record.sentiments?.length),
        sorter: false,
      },
      ...questionColumns.map(({ id, title }) => ({
        title,
        dataIndex: id,
        visible: false,
      })),
    ];
  }, [$t, themes, sentiments, questionColumns]);

  const filteredComments = useMemo(() => {
    if (!currentFilters) {
      return comments;
    }
    const filters = Object.entries(currentFilters).filter(([, values]) => values);
    if (!filters.length) {
      return comments;
    }
    return comments.filter(row => filters.every(([key, values]) => {
      const column = columns.find(col => col.dataIndex === key);
      return column?.onFilter && values.some(v => column.onFilter(v, row));
    }));
  }, [comments, currentFilters, columns]);

  const commentsWithSelectedTag = selectedTag ? filteredComments.filter(r => r.themes?.includes(selectedTag.id)) : [];
  const commentsWithoutSelectedTag = selectedTag ? filteredComments.filter(r => !r.themes?.includes(selectedTag.id)) : [];
  const removeCount = commentsWithSelectedTag.length;
  const addCount = commentsWithoutSelectedTag.length;
  const loading = tagCommentsLoading || untagCommentsLoading || createThemeLoading || removeThemeLoading;

  const onTableChange = (pagination, filters) => {
    if (!equal(currentFilters, filters)) {
      setCurrentFilters(filters);
    }
  };

  const actionsMenuItems = record => [{
    key: 'reportComment',
    label: <FormattedMessage {...messages.reportComment} />,
    onClick: () => reportComment(record),
    icon: null,
  }, {
    key: 'translate',
    label: <FormattedMessage {...messages.translateComment} />,
    onClick: () => openGoogleTranslate({ text: record.text }),
    icon: null,
  }];

  return (
    <>
      <Row gutter={[16, 16]} justify="space-between">
        <Col>
          <Space>
            <SelectOrAdd
              style={{ width: 300 }}
              loading={loading}
              placeholder={$t(messages.selectTag)}
              allowClear
              onAdd={handleAddTheme}
              onChange={setSelectedTagId}
              value={selectedTagId}
              addButtonLabel={$t(messages.addTheme)}
            >
              <OptGroup label={$t(messages.sentiment)}>
                {sentiments.map(tag => <Option key={tag.id} value={tag.id}>{messages[tag.text] ? <FormattedMessage {...messages[tag.text]} /> : tag.text}</Option>)}
              </OptGroup>
              <OptGroup label={$t(messages.theme)}>
                {themes.map(tag => <Option key={tag.id} value={tag.id}>{tag.text}</Option>)}
              </OptGroup>
            </SelectOrAdd>
            <Button type="primary" onClick={() => handleTagsMutation({ action: 'add', questionResponseIds: commentsWithoutSelectedTag.map(c => c.id) })} disabled={!selectedTag || !addCount} loading={tagCommentsLoading} icon={<PlusOutlined />}>
              <FormattedMessage {...messages.addTagToAll} values={{ count: addCount }} />
            </Button>
            <Button type="primary" danger onClick={() => handleTagsMutation({ action: 'remove', questionResponseIds: commentsWithSelectedTag.map(c => c.id) })} disabled={!selectedTag || !removeCount} loading={untagCommentsLoading} icon={<MinusOutlined />}>
              <FormattedMessage {...messages.removeTagFromAll} values={{ count: removeCount }} />
            </Button>
            <Button onClick={() => setRenameTag(true)} disabled={selectedTag?.type !== 'THEME'} icon={<EditOutlined />}>
              <FormattedMessage {...messages.editTag} />
            </Button>
            <Button type="primary" danger onClick={handleRemoveTheme} disabled={!selectedTag || selectedTag.type !== 'THEME' || selectedTag.commentCount > 0} loading={removeThemeLoading} icon={<DeleteOutlined />}>
              {selectedTag?.commentCount > 0 ? <FormattedMessage {...messages.tagUsed} values={{ count: selectedTag.commentCount }} /> : <FormattedMessage {...messages.removeTag} />}
            </Button>

            {loading && <LoadingOutlined />}
          </Space>
        </Col>
        <Col span={24}>
          <Table
            size="middle"
            bordered
            loading={tagsLoading || questionsLoading || responsesLoading}
            columns={columns}
            dataSource={filteredComments}
            actionsMenuItems={actionsMenuItems}
            onChange={onTableChange}
            scroll={{
              y: 'calc(100vh - 360px)',
            }}
            tableLayout="auto"
            className="break-word"
            onVisibleColumnsChange={onVisibleColumnsChange}
          />
        </Col>
      </Row>
      <RenameTag tag={renameTag && selectedTag} onCancel={() => setRenameTag(false)} onDone={onRenameTheme} />
    </>
  );
};

export default TagCommentsManualTab;
